import { useState, useEffect, useRef } from 'react';

export interface UseStreamingTextOptions {
  /**
   * Speed of animation in milliseconds per word
   * @default 50
   */
  speed?: number;
  /**
   * Whether to animate word-by-word (true) or character-by-character (false)
   * @default true
   */
  wordByWord?: boolean;
  /**
   * Whether to skip animation if user prefers reduced motion
   * @default true
   */
  respectReducedMotion?: boolean;
  /**
   * Whether to start animation immediately
   * @default true
   */
  autoStart?: boolean;
}

/**
 * Hook for streaming text animation (character-by-character or word-by-word)
 * Similar to ChatGPT's smooth text reveal effect
 */
export function useStreamingText(
  text: string,
  options: UseStreamingTextOptions = {}
): string {
  const {
    speed = 50,
    wordByWord = true,
    respectReducedMotion = true,
    autoStart = true,
  } = options;

  const [displayedText, setDisplayedText] = useState('');
  const animationRef = useRef<number | null>(null);
  const textRef = useRef(text);
  const indexRef = useRef(0);
  const lastUpdateTimeRef = useRef<number>(0);

  // Check for reduced motion preference
  const prefersReducedMotion = respectReducedMotion && typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  useEffect(() => {
    // Update text ref when text changes
    textRef.current = text;
    
    // If text changes, reset animation
    if (text !== displayedText && text.length > displayedText.length) {
      indexRef.current = displayedText.length;
    }
  }, [text, displayedText]);

  useEffect(() => {
    // Skip animation if reduced motion is preferred or autoStart is false
    if (prefersReducedMotion || !autoStart) {
      setDisplayedText(text);
      return;
    }

    // If text is already complete, skip animation
    if (text === displayedText && text.length > 0) {
      return;
    }

    // If text is empty, reset
    if (!text) {
      setDisplayedText('');
      indexRef.current = 0;
      return;
    }

    // If text changed completely (not just extended), reset
    if (text.length < displayedText.length || !text.startsWith(displayedText)) {
      setDisplayedText('');
      indexRef.current = 0;
    }

    // Start animation
    const startIndex = displayedText.length;
    indexRef.current = startIndex;

    if (typeof window === 'undefined') {
      setDisplayedText(text);
      return;
    }

    lastUpdateTimeRef.current = performance.now();
    // Use slightly faster frame check for smoother updates (Gemini style)
    const frameDuration = Math.max(16, speed); // Minimum 16ms for 60fps

    const animate = (currentTime: number) => {
      if (indexRef.current >= text.length) {
        setDisplayedText(text);
        return;
      }

      const elapsed = currentTime - lastUpdateTimeRef.current;
      
      // Smooth incremental updates - Gemini style
      if (elapsed >= frameDuration) {
        if (wordByWord) {
          // Word-by-word animation (more natural, like Gemini)
          // Find the next word boundary
          const remaining = text.slice(indexRef.current);
          const match = remaining.match(/^\S*\s*/);
          if (match) {
            indexRef.current += match[0].length;
          } else {
            // Last word (no trailing space)
            indexRef.current = text.length;
          }
        } else {
          // Character-by-character animation
          indexRef.current++;
        }

        setDisplayedText(text.slice(0, indexRef.current));
        lastUpdateTimeRef.current = currentTime;
      }

      // Continue animation - always schedule next frame for smooth updates
      animationRef.current = requestAnimationFrame(animate) as unknown as number;
    };

    // Use requestAnimationFrame for smooth animation
    animationRef.current = requestAnimationFrame(animate) as unknown as number;

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [text, speed, wordByWord, prefersReducedMotion, autoStart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return displayedText;
}

